import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, FileDown, Handshake } from "lucide-react";
import { scrollToSection } from "../../lib/scroll";
import { useLang } from "../../i18n/LanguageContext";

const TARGET = new Date("2027-05-03T09:00:00+02:00").getTime();

const line = {
  hidden: { y: "115%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.95, ease: [0.22, 1, 0.36, 1], delay: 0.15 + i * 0.14 } }),
};

function useCountdown() {
  const [t, setT] = useState({ g: "--", h: "--", m: "--", s: "--" });
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, TARGET - Date.now());
      setT({
        g: String(Math.floor(diff / 86400000)).padStart(3, "0"),
        h: String(Math.floor(diff / 3600000) % 24).padStart(2, "0"),
        m: String(Math.floor(diff / 60000) % 60).padStart(2, "0"),
        s: String(Math.floor(diff / 1000) % 60).padStart(2, "0"),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return t;
}

const HERO_IMG = "/img/track.jpg";

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const c = useCountdown();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  return (
    <section id="hero" ref={ref} className="noise-overlay relative flex min-h-screen items-center overflow-hidden bg-vg-black pt-[72px]">
      <div className="absolute inset-0 bg-grid" aria-hidden="true" />
      <motion.div
        style={{ y: glowY }}
        className="absolute -left-44 top-1/3 h-[560px] w-[560px] rounded-full bg-vg-green/15 blur-[140px]"
        aria-hidden="true"
      />
      <div className="absolute -right-24 top-0 h-[420px] w-[420px] rounded-full bg-vg-red/10 blur-[120px]" aria-hidden="true" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-5 py-16 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:py-10">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-8 flex flex-wrap items-center gap-3">
            <span className="hud-chip">
              <span className="flex gap-1" aria-hidden="true">
                <span className="h-2 w-1 bg-vg-green" />
                <span className="h-2 w-1 bg-vg-white" />
                <span className="h-2 w-1 bg-vg-red" />
              </span>
              {t.hero.chip1}
            </span>
            <span className="hud-chip hidden sm:inline-flex">{t.hero.chip2}</span>
          </motion.div>

          <h1 className="font-display text-5xl font-bold uppercase leading-[0.95] tracking-wide text-vg-white sm:text-6xl lg:text-7xl xl:text-[5.4rem]">
            {t.hero.lines.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                  {l.t}
                  {l.g && <span className="text-vg-green">{l.g}</span>}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 max-w-2xl text-base leading-relaxed text-vg-muted sm:text-lg"
          >
            {t.hero.sub}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-3 font-mono text-[11px] uppercase tracking-[0.24em] text-vg-green/80"
          >
            {t.hero.kicker}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/scheda-adesione`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-adesione"
              className="inline-flex items-center gap-2 rounded-full bg-vg-green px-7 py-3.5 font-display text-base font-semibold uppercase tracking-wider text-vg-black transition-all duration-200 hover:shadow-[0_0_36px_rgba(16,185,129,0.55)]"
            >
              <FileDown className="h-4 w-4" />
              {t.hero.cta1}
            </a>
            <button
              onClick={() => scrollToSection("sponsor")}
              data-testid="hero-cta-sponsor"
              className="inline-flex items-center gap-2 rounded-full border border-vg-white/25 px-7 py-3.5 font-display text-base font-semibold uppercase tracking-wider text-vg-white transition-all duration-200 hover:border-vg-green hover:text-vg-green"
            >
              <Handshake className="h-4 w-4" />
              {t.hero.cta2}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-12 flex flex-wrap items-center gap-3 sm:gap-4"
            data-testid="hero-countdown"
          >
            {t.hero.countdown.map((cd, i) => (
              <div key={cd.l} className="flex min-w-[76px] flex-col items-center rounded-lg border border-white/10 bg-vg-card/70 px-4 py-3 backdrop-blur-md">
                <span data-testid={cd.tid} className="font-mono text-2xl font-medium text-vg-green tabular-nums">
                  {[c.g, c.h, c.m, c.s][i]}
                </span>
                <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-vg-muted">{cd.l}</span>
              </div>
            ))}
            <span className="ml-1 hidden items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted md:inline-flex">
              <MapPin className="h-3.5 w-3.5 text-vg-red" />
              Misano Adriatico (RN)
            </span>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="clip-frame relative overflow-hidden border border-vg-green/25 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
              <motion.img
                style={{ y: imgY, scale: 1.15 }}
                src={HERO_IMG}
                alt="Prototipo elettrico EVGP in pista"
                className="h-[380px] w-full object-cover sm:h-[440px] lg:h-[560px]"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-vg-black/80 via-transparent to-vg-black/30" aria-hidden="true" />
              <span className="hud-chip absolute left-4 top-4">{t.hero.imgChips.soc}</span>
              <span className="hud-chip animate-pulse-glow absolute bottom-4 right-4">
                <span className="h-1.5 w-1.5 rounded-full bg-vg-green" />
                {t.hero.imgChips.telemetry}
              </span>
              <span className="hud-chip absolute right-4 top-4 hidden sm:inline-flex">{t.hero.imgChips.limiter}</span>
            </div>
            <div className="mt-4 flex items-center justify-between border border-white/10 bg-vg-card/80 px-5 py-3 backdrop-blur-md">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-vg-muted">{t.hero.imgCaption}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-vg-green">{t.hero.imgTag}</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
